import { AlertCircle } from 'lucide-react';
import React, { useState } from 'react';
import { INVALID_USERNAME_CHARS_REGEX } from '../constants';

interface UsernameInputProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    className?: string;
    disabled?: boolean;
    prefix?: React.ReactNode;
}

export const UsernameInput: React.FC<UsernameInputProps> = ({ 
    value, 
    onChange, 
    placeholder = "e.g. cheekycharlie",
    className = "",
    disabled = false,
    prefix
}) => {
    const [shake, setShake] = useState(false);
    const [showWarning, setShowWarning] = useState(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const rawValue = e.target.value.toLowerCase();
        
        // Sanitize: Only lowercase letters, numbers, and underscores
        const sanitizedValue = rawValue.replace(INVALID_USERNAME_CHARS_REGEX, '');

        // If the value changed during sanitization (meaning invalid chars were present)
        if (rawValue !== sanitizedValue) {
            setShake(true);
            setShowWarning(true);
            setTimeout(() => setShake(false), 500);
            setTimeout(() => setShowWarning(false), 2000);
        } else {
             setShowWarning(false);
        }

        // Always update with the sanitized value
        onChange(sanitizedValue);
    };

    return (
        <div className={`relative w-full flex items-center transition-all ${shake ? 'animate-shake border-brand-red text-brand-red' : ''} ${className}`}>
            {/* Prefix (e.g. @ symbol) handled as a flex item */}
            {prefix && (
                <div className="mr-1 text-inherit opacity-70 font-bold select-none">
                    {prefix}
                </div>
            )}
            
            <input 
                type="text" 
                value={value}
                onChange={handleChange}
                disabled={disabled}
                placeholder={placeholder}
                className="flex-1 bg-transparent border-none outline-none p-0 focus:ring-0 text-inherit placeholder:text-gray-400 w-full"
            />
            
            {showWarning && (
                <div className="absolute right-0 -bottom-6 flex items-center gap-1 text-[10px] sm:text-xs font-bold text-brand-red animate-in fade-in slide-in-from-top-1">
                    <AlertCircle size={12} />
                    <span>Letters, numbers & underscores only</span>
                </div>
            )}
            
            <style>{`
                @keyframes shake {
                    0%, 100% { transform: translateX(0); }
                    25% { transform: translateX(-5px); }
                    75% { transform: translateX(5px); }
                }
                .animate-shake {
                    animation: shake 0.3s cubic-bezier(.36,.07,.19,.97) both;
                }
            `}</style>
        </div>
    );
};
